import { useEffect, useRef, useState } from 'react';

const FRAME_INTERVAL_MS = 1000 / 30;
const PULSE_PERIOD_MS = 1400;

interface RefreshableSigma {
  refresh: () => void;
}

/**
 * rAF loop yielding a 0..1 pulse value for highlight animation.
 * Capped to ~30fps to stay smooth on the 47k-node graph.
 */
export function useHighlightAnimation(sigma: RefreshableSigma | null, active: boolean): number {
  const [pulse, setPulse] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef(0);

  useEffect(() => {
    if (!active || !sigma) {
      if (pulse !== 0) setPulse(0);
      return undefined;
    }
    const start = performance.now();

    const tick = (now: number): void => {
      if (now - lastTickRef.current >= FRAME_INTERVAL_MS) {
        const phase = ((now - start) % PULSE_PERIOD_MS) / PULSE_PERIOD_MS;
        const next = (Math.sin(phase * Math.PI * 2) + 1) / 2;
        lastTickRef.current = now;
        setPulse(next);
        sigma.refresh();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return (): void => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // pulse intentionally excluded; reading it would restart rAF.
  }, [active, sigma]);

  return pulse;
}
